/**
 * ---------------------------------------------------------------------------------------------------------------
 * GLOBAL PRODUCT OPTIONS
 * ---------------------------------------------------------------------------------------------------------------
 *
 * Adds re-usable, tag-driven option sets to a product page without replacing any of the theme's existing
 * product-form / cart-drawer machinery.
 *
 * How it plugs into Prestige:
 *
 *   - The component renders inside the theme's existing <product-form><form> markup, so every select is a normal
 *     form field and is picked up by the theme's own FormData.
 *   - It listens for `submit` on the <form> in the capture phase. If (and only if) at least one global option is
 *     selected, it calls `stopPropagation()` so the theme's own <product-form> bubble-phase handler does not run,
 *     and performs a single `/cart/add.js` request with an `items` array instead.
 *   - It then dispatches exactly the same events the theme's own ProductForm dispatches
 *     (`cart:prepare-bundled-sections`, `theme:loading:start` / `theme:loading:end`, `variant:add`, `cart:change`,
 *     `cart:error`, `cart:refresh`), so the existing cart drawer, cart count, cart dot, free-shipping bar,
 *     cart-note and quick-buy modal all react exactly as they already do.
 *   - When nothing is selected it does not stop propagation at all: the theme's stock single-item add-to-cart
 *     runs untouched.
 *
 * Nothing here runs on a product that has no `global_option:` tag, because the Liquid snippet only emits the
 * <global-product-options> element for tagged products.
 */

(function () {
  'use strict';

  if (window.customElements.get('global-product-options')) {
    return;
  }

  /* ------------------------------------------------------------------------------------------------------------
   * Constants & shared state
   * --------------------------------------------------------------------------------------------------------- */

  var INSTANCE_PROPERTY = '_global_options_instance';
  var ROLE_PROPERTY = '_global_options_role';
  var PARENT_PROPERTY = '_global_options_parent_variant';
  var PARENT_ID_STORAGE_KEY = 'globalProductOptions:parentIdSupported';

  /** Map<handle, Promise<productJson>> — one network request per source product per page session. */
  var sourceProductCache = new Map();

  /**
   * Whether Shopify accepted the native `parent_id` nested-line field on this storefront.
   * null = not tested yet. Persisted per session so we never pay for a failed probe twice.
   */
  var parentIdSupported = null;

  try {
    var storedSupport = window.sessionStorage.getItem(PARENT_ID_STORAGE_KEY);
    if (storedSupport !== null) {
      parentIdSupported = storedSupport === 'true';
    }
  } catch (error) {
    /* Storage unavailable (private mode / blocked cookies) — fall back to per-page-load probing. */
  }

  function rememberParentIdSupport(supported) {
    parentIdSupported = supported;
    try {
      window.sessionStorage.setItem(PARENT_ID_STORAGE_KEY, supported ? 'true' : 'false');
    } catch (error) {
      /* no-op */
    }
  }

  /* ------------------------------------------------------------------------------------------------------------
   * Small helpers
   * --------------------------------------------------------------------------------------------------------- */

  function rootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function warn() {
    if (typeof console === 'undefined' || typeof console.warn !== 'function') {
      return;
    }
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[global-product-options]');
    console.warn.apply(console, args);
  }

  function generateInstanceId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'gpo-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /**
   * Fetch a source product through the locale-aware Ajax product endpoint. Deduplicated per handle so that several
   * component instances (main product + sticky bar + quick buy) referencing the same source product only ever
   * trigger one request.
   */
  function fetchSourceProduct(handle) {
    if (!sourceProductCache.has(handle)) {
      var url = rootUrl() + 'products/' + encodeURIComponent(handle) + '.js';

      var promise = fetch(url, { headers: { Accept: 'application/json' } }).then(function (response) {
        if (!response.ok) {
          throw new Error('Source product "' + handle + '" responded with ' + response.status);
        }
        return response.json();
      });

      // Prevent "unhandled promise rejection" noise; every consumer attaches its own catch.
      promise.catch(function () {});

      sourceProductCache.set(handle, promise);
    }

    return sourceProductCache.get(handle);
  }

  /**
   * Builds a money formatter from two Liquid-rendered samples ({{ 0 | money }} and {{ 123456 | money }}), so the
   * preview uses the storefront's real presentment currency, symbol placement, separators and decimal count
   * without hard-coding anything.
   */
  function createMoneyFormatter(zeroSample, largeSample) {
    if (!zeroSample) {
      return null;
    }

    var numberPattern = /\d[\d.,\s  ']*\d|\d/;
    var zeroMatch = zeroSample.match(numberPattern);

    if (!zeroMatch) {
      return null;
    }

    var prefix = zeroSample.slice(0, zeroMatch.index);
    var suffix = zeroSample.slice(zeroMatch.index + zeroMatch[0].length);
    var decimals = 0;
    var decimalSeparator = '.';
    var decimalMatch = zeroMatch[0].match(/^0([.,])(0+)$/);

    if (decimalMatch) {
      decimalSeparator = decimalMatch[1];
      decimals = decimalMatch[2].length;
    }

    var thousandsSeparator = '';
    var largeMatch = largeSample ? largeSample.match(numberPattern) : null;

    if (largeMatch) {
      var integerPart = largeMatch[0];

      if (decimals > 0) {
        var decimalIndex = integerPart.lastIndexOf(decimalSeparator);
        if (decimalIndex > -1) {
          integerPart = integerPart.slice(0, decimalIndex);
        }
      }

      var separatorMatch = integerPart.match(/[^\d]/);
      if (separatorMatch) {
        thousandsSeparator = separatorMatch[0];
      }
    }

    return function formatMoney(cents) {
      var amount = Number(cents);

      if (!isFinite(amount)) {
        amount = 0;
      }

      var isNegative = amount < 0;
      var parts = (Math.abs(amount) / 100).toFixed(decimals).split('.');
      var integers = parts[0];
      var fraction = parts[1];

      if (thousandsSeparator) {
        integers = integers.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
      }

      var body = decimals > 0 ? integers + decimalSeparator + fraction : integers;

      return (isNegative ? '-' : '') + prefix + body + suffix;
    };
  }

  /* ------------------------------------------------------------------------------------------------------------
   * The component
   * --------------------------------------------------------------------------------------------------------- */

  var GlobalProductOptions = class extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
      this._hydrated = false;
      this._isSubmitting = false;
      this._formatMoney = null;
      this._mainPrice = 0;
      this._onSubmitCapture = this._onSubmitCapture.bind(this);
      this._onSelectChange = this._onSelectChange.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
    }

    connectedCallback() {
      // The element may be moved / re-connected (theme editor, product re-render). Always rebuild listeners,
      // but only hydrate remote data once.
      this._abortController = new AbortController();

      this._formatMoney = createMoneyFormatter(
        this.getAttribute('data-money-zero-sample'),
        this.getAttribute('data-money-large-sample')
      );

      this._mainPrice = parseInt(this.getAttribute('data-main-price'), 10) || 0;

      var form = this.form;

      if (!form) {
        warn('No form found for attribute form="' + this.getAttribute('form') + '"; component disabled.');
        return;
      }

      var signal = this._abortController.signal;

      form.addEventListener('submit', this._onSubmitCapture, { capture: true, signal: signal });
      form.addEventListener('variant:change', this._onVariantChange, { signal: signal });
      this.addEventListener('change', this._onSelectChange, { signal: signal });

      if (!this._hydrated) {
        this._hydrated = true;
        this._hydratePendingSets();
      }

      this._updateSummary();
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
    }

    /* ---------------------------------------------------------------------------------------------------------
     * Accessors
     * ------------------------------------------------------------------------------------------------------ */

    get form() {
      var formId = this.getAttribute('form');
      return formId ? document.forms[formId] || null : null;
    }

    get sets() {
      return Array.from(this.querySelectorAll('[data-global-option-set]'));
    }

    get selects() {
      return Array.from(this.querySelectorAll('[data-global-option-select]'));
    }

    /* ---------------------------------------------------------------------------------------------------------
     * Hydration of sets that Liquid could not resolve (all_products is capped at 20 lookups per template)
     * ------------------------------------------------------------------------------------------------------ */

    _hydratePendingSets() {
      var self = this;

      this.sets
        .filter(function (set) {
          return set.getAttribute('data-needs-fetch') === 'true';
        })
        .forEach(function (set) {
          var handle = set.getAttribute('data-source-handle');

          if (!handle) {
            self._disableSet(set, 'Missing source handle');
            return;
          }

          fetchSourceProduct(handle)
            .then(function (productJson) {
              self._populateSet(set, productJson);
            })
            .catch(function (error) {
              warn('Could not load option source product "' + handle + '".', error);
              // The main product must remain purchasable: an optional set that failed to load is removed,
              // a required one is surfaced as an error so the customer is not silently under-charged.
              if (set.getAttribute('data-required') === 'true') {
                self._disableSet(set, self.getAttribute('data-unavailable-error'));
              } else {
                set.remove();
                self._updateSummary();
              }
            });
        });
    }

    _populateSet(set, productJson) {
      var select = set.querySelector('[data-global-option-select]');
      var label = set.querySelector('[data-global-option-label]');

      if (!select || !productJson || !Array.isArray(productJson.variants)) {
        this._disableSet(set, this.getAttribute('data-unavailable-error'));
        return;
      }

      var placeholder = this.getAttribute('data-placeholder') || 'No';
      var soldOutLabel = this.getAttribute('data-sold-out-label') || 'Sold out';
      var hasOnlyDefaultVariant =
        productJson.variants.length === 1 && productJson.variants[0].title === 'Default Title';
      var setTitle = productJson.title || select.getAttribute('data-source-handle');

      // Update the visible heading and the line item property name to the real product title.
      if (label) {
        var requiredMarker = label.querySelector('.global-product-options__required-marker');
        label.textContent = setTitle;
        if (requiredMarker) {
          label.appendChild(requiredMarker);
        }
      }

      select.setAttribute('name', 'properties[' + setTitle + ']');

      // Rebuild the options safely with DOM APIs (no innerHTML, no injection surface).
      while (select.firstChild) {
        select.removeChild(select.firstChild);
      }

      var placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = placeholder;
      placeholderOption.selected = true;
      placeholderOption.setAttribute('data-variant-id', '');
      placeholderOption.setAttribute('data-price', '0');
      select.appendChild(placeholderOption);

      for (var index = 0; index < productJson.variants.length; index++) {
        var variant = productJson.variants[index];
        var optionLabel = hasOnlyDefaultVariant ? setTitle : variant.title;
        var price = typeof variant.price === 'number' ? variant.price : parseInt(variant.price, 10) || 0;
        var formattedPrice = this._formatMoney ? this._formatMoney(price) : '';
        var text = optionLabel;

        if (price > 0 && formattedPrice) {
          text += ' (+' + formattedPrice + ')';
        }

        if (!variant.available) {
          text += ' — ' + soldOutLabel;
        }

        var option = document.createElement('option');
        option.value = optionLabel;
        option.textContent = text;
        option.setAttribute('data-variant-id', String(variant.id));
        option.setAttribute('data-price', String(price));
        option.setAttribute('data-price-formatted', formattedPrice);
        option.setAttribute('data-available', variant.available ? 'true' : 'false');
        option.disabled = !variant.available;

        select.appendChild(option);
      }

      select.removeAttribute('aria-busy');
      set.removeAttribute('data-needs-fetch');
      this._updateSummary();
    }

    _disableSet(set, message) {
      var select = set.querySelector('[data-global-option-select]');

      if (select) {
        select.disabled = true;
        select.removeAttribute('aria-busy');
      }

      if (message) {
        this._showSetError(set, message);
      }
    }

    /* ---------------------------------------------------------------------------------------------------------
     * Selection state
     * ------------------------------------------------------------------------------------------------------ */

    /**
     * Only variant-backed sets are returned. The free, text-only sets from the shop metafields carry no
     * `data-variant-id`, ride along on the main product as ordinary line item properties, and must never become
     * a cart line of their own — otherwise they would add a price.
     *
     * @returns {Array<{variantId: number, price: number, label: string, propertyName: string, handle: string}>}
     */
    getSelections() {
      var selections = [];

      this.selects.forEach(function (select) {
        if (select.disabled || select.getAttribute('data-property-only') === 'true') {
          return;
        }

        var option = select.options[select.selectedIndex];

        if (!option) {
          return;
        }

        var variantId = parseInt(option.getAttribute('data-variant-id'), 10);

        if (!variantId || option.disabled) {
          return;
        }

        var name = select.getAttribute('name') || '';
        var propertyMatch = name.match(/^properties\[(.*)\]$/);

        selections.push({
          variantId: variantId,
          price: parseInt(option.getAttribute('data-price'), 10) || 0,
          label: option.value,
          propertyName: propertyMatch ? propertyMatch[1] : name,
          handle: select.getAttribute('data-source-handle') || ''
        });
      });

      return selections;
    }

    _onSelectChange(event) {
      var select = event.target.closest ? event.target.closest('[data-global-option-select]') : null;

      if (!select || !this.contains(select)) {
        return;
      }

      var set = select.closest('[data-global-option-set]');

      if (set) {
        this._clearSetError(set);
      }

      this._updateSummary();
    }

    _onVariantChange(event) {
      var variant = event && event.detail ? event.detail.variant : null;

      if (variant && typeof variant.price === 'number') {
        this._mainPrice = variant.price;
        this.setAttribute('data-main-price', String(variant.price));
        this._updateSummary();
      }
    }

    /* ---------------------------------------------------------------------------------------------------------
     * Storefront-only price preview. The real prices always come from Shopify's own variants at checkout.
     * ------------------------------------------------------------------------------------------------------ */

    _updateSummary() {
      var summary = this.querySelector('[data-global-options-summary]');

      if (!summary) {
        return;
      }

      if (!this._formatMoney) {
        summary.hidden = true;
        return;
      }

      var selections = this.getSelections();

      if (selections.length === 0) {
        summary.hidden = true;
        return;
      }

      var addons = selections.reduce(function (total, selection) {
        return total + selection.price;
      }, 0);

      var baseElement = summary.querySelector('[data-global-options-summary-base]');
      var addonsElement = summary.querySelector('[data-global-options-summary-addons]');
      var totalElement = summary.querySelector('[data-global-options-summary-total]');

      if (baseElement) {
        baseElement.textContent = this._formatMoney(this._mainPrice);
      }

      if (addonsElement) {
        addonsElement.textContent = '+' + this._formatMoney(addons);
      }

      if (totalElement) {
        totalElement.textContent = this._formatMoney(this._mainPrice + addons);
      }

      summary.hidden = false;
    }

    /* ---------------------------------------------------------------------------------------------------------
     * Validation
     * ------------------------------------------------------------------------------------------------------ */

    _showSetError(set, message) {
      var errorElement = set.querySelector('[data-global-option-error]');
      var select = set.querySelector('[data-global-option-select]');

      if (errorElement) {
        errorElement.textContent = message;
        errorElement.hidden = false;
      }

      if (select) {
        select.setAttribute('aria-invalid', 'true');
      }

      set.classList.add('global-product-options__field--has-error');
    }

    _clearSetError(set) {
      var errorElement = set.querySelector('[data-global-option-error]');
      var select = set.querySelector('[data-global-option-select]');

      if (errorElement) {
        errorElement.textContent = '';
        errorElement.hidden = true;
      }

      if (select) {
        select.removeAttribute('aria-invalid');
      }

      set.classList.remove('global-product-options__field--has-error');
    }

    /**
     * @returns {boolean} true when every required set has a valid, available selection.
     */
    validate() {
      var self = this;
      var message = this.getAttribute('data-required-error') || 'Please select an option';
      var firstInvalidSelect = null;

      this.sets.forEach(function (set) {
        self._clearSetError(set);

        if (set.getAttribute('data-required') !== 'true') {
          return;
        }

        var select = set.querySelector('[data-global-option-select]');

        if (!select) {
          return;
        }

        var option = select.options[select.selectedIndex];
        var variantId = option ? parseInt(option.getAttribute('data-variant-id'), 10) : 0;

        if (!variantId || option.disabled || select.disabled) {
          self._showSetError(set, message);

          if (!firstInvalidSelect) {
            firstInvalidSelect = select;
          }
        }
      });

      if (firstInvalidSelect) {
        firstInvalidSelect.focus();
        return false;
      }

      return true;
    }

    /* ---------------------------------------------------------------------------------------------------------
     * Add to cart
     * ------------------------------------------------------------------------------------------------------ */

    /**
     * A set left on its "No" default must not reach the cart at all. A nameless field is skipped by FormData, so
     * dropping the `name` for the duration of this submit keeps the untouched sets out of every payload built
     * from the form — the theme's own stock handler (which runs in the bubble phase, synchronously after this
     * one) included — and no blank line item property is ever posted.
     *
     * The name is put back on the next tick. Unlike `disabled`, this changes nothing the customer can see and
     * never moves the focus.
     */
    _suspendEmptySelects() {
      var suspended = [];

      this.selects.forEach(function (select) {
        var name = select.getAttribute('name');

        if (!name || select.value !== '') {
          return;
        }

        suspended.push({ select: select, name: name });
        select.removeAttribute('name');
      });

      if (suspended.length === 0) {
        return;
      }

      window.setTimeout(function () {
        suspended.forEach(function (entry) {
          entry.select.setAttribute('name', entry.name);
        });
      }, 0);
    }

    _onSubmitCapture(event) {
      var form = this.form;

      if (!form || event.target !== form) {
        return;
      }

      // The theme marks busy submit buttons with aria-busy; mirror that guard plus our own re-entrancy flag.
      if (this._isSubmitting || (event.submitter && event.submitter.getAttribute('aria-busy') === 'true')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      var hasSets = this.sets.length > 0;

      if (!hasSets) {
        return; // Nothing to do — let the theme handle the submission exactly as before.
      }

      if (!this.validate()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // After validation (so a required empty set can still be reported and focused), and before the early
      // return below that hands the submission back to the theme's own handler.
      this._suspendEmptySelects();

      var selections = this.getSelections();

      if (selections.length === 0) {
        // No option chosen: the stock theme flow is a perfect fit, so we stay out of the way entirely.
        return;
      }

      if (!form.checkValidity()) {
        event.preventDefault();
        event.stopPropagation();
        form.reportValidity();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this._addToCart(form, selections);
    }

    _buildPayload(form, selections, includeParentId) {
      var formData = new FormData(form);
      var mainVariantId = parseInt(formData.get('id'), 10);

      if (!mainVariantId) {
        return null;
      }

      var quantity = parseInt(formData.get('quantity'), 10);

      if (!quantity || quantity < 1) {
        quantity = 1;
      }

      var instanceId = generateInstanceId();
      var mainProperties = {};

      formData.forEach(function (value, key) {
        var match = key.match(/^properties\[(.*)\]$/);

        if (!match) {
          return;
        }

        if (typeof value !== 'string') {
          // File uploads cannot travel in a JSON /cart/add.js body. This theme ships no file line-item
          // property, but an app could add one — skip it rather than producing an unserialisable payload.
          warn('Skipping non-text line item property "' + match[1] + '" in the global-options add-to-cart payload.');
          return;
        }

        if (value.trim() === '') {
          return; // Skip untouched placeholders and empty optional fields.
        }

        mainProperties[match[1]] = value;
      });

      mainProperties[INSTANCE_PROPERTY] = instanceId;
      mainProperties[ROLE_PROPERTY] = 'parent';

      var mainItem = {
        id: mainVariantId,
        quantity: quantity,
        properties: mainProperties
      };

      var sellingPlan = formData.get('selling_plan');

      if (sellingPlan) {
        mainItem.selling_plan = parseInt(sellingPlan, 10) || sellingPlan;
      }

      var items = [mainItem];

      selections.forEach(function (selection) {
        var childProperties = {};

        childProperties[INSTANCE_PROPERTY] = instanceId;
        childProperties[ROLE_PROPERTY] = 'child';
        childProperties[PARENT_PROPERTY] = String(mainVariantId);

        var childItem = {
          id: selection.variantId,
          quantity: quantity,
          properties: childProperties
        };

        if (includeParentId) {
          childItem.parent_id = mainVariantId;
        }

        items.push(childItem);
      });

      return { items: items, instanceId: instanceId, mainVariantId: mainVariantId };
    }

    async _addToCart(form, selections) {
      var self = this;

      this._isSubmitting = true;

      var submitButtons = Array.from(form.elements).filter(function (element) {
        return element.type === 'submit';
      });

      submitButtons.forEach(function (button) {
        button.setAttribute('aria-busy', 'true');
      });

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      var sectionsToBundle = [];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      var useNativeNesting = this.getAttribute('data-native-nesting') !== 'false';
      var attemptParentId = useNativeNesting && parentIdSupported !== false;
      var onSuccessDo = new FormData(form).get('on_success');

      var finish = function () {
        submitButtons.forEach(function (button) {
          button.removeAttribute('aria-busy');
        });
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        self._isSubmitting = false;
      };

      var fail = function (message) {
        form.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: message || self.getAttribute('data-generic-error') }
          })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      };

      try {
        var payload = this._buildPayload(form, selections, attemptParentId);

        if (!payload) {
          warn('Could not resolve the selected main product variant; aborting.');
          finish();
          fail(this.getAttribute('data-generic-error'));
          return;
        }

        var request = {
          items: payload.items,
          sections: sectionsToBundle.join(','),
          sections_url: rootUrl() + 'variants/' + payload.mainVariantId
        };

        var response = await this._postCartAdd(request);
        var responseJson = await response.json();

        // `/cart/add.js` with an `items` array is atomic, so a rejected probe cannot have added anything.
        // If the only unknown was `parent_id`, retry once without it and remember the outcome for the session.
        if (!response.ok && attemptParentId) {
          var fallbackPayload = this._buildPayload(form, selections, false);

          if (fallbackPayload) {
            var fallbackResponse = await this._postCartAdd({
              items: fallbackPayload.items,
              sections: sectionsToBundle.join(','),
              sections_url: rootUrl() + 'variants/' + fallbackPayload.mainVariantId
            });
            var fallbackJson = await fallbackResponse.json();

            if (fallbackResponse.ok) {
              rememberParentIdSupport(false);
              warn('This storefront rejected native nested cart lines (parent_id); using private line-item properties instead.');
              response = fallbackResponse;
              responseJson = fallbackJson;
            }
          }
        } else if (response.ok && attemptParentId && parentIdSupported === null) {
          rememberParentIdSupport(true);
        }

        finish();

        if (!response.ok) {
          fail(responseJson && responseJson.description ? responseJson.description : null);
          return;
        }

        // Mirror the theme's own ProductForm: refresh the cart object, graft the bundled sections onto it and
        // dispatch the exact same events so the existing drawer / count / bubble / free-shipping bar update.
        var cartContent;

        try {
          cartContent = await (await fetch(rootUrl() + 'cart.js')).json();
        } catch (cartError) {
          // The items ARE in the cart; only the refresh failed. Ask the theme to re-render from the server
          // instead of showing a misleading error to the customer.
          warn('Cart was updated but the cart state could not be refreshed.', cartError);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        cartContent.sections = responseJson.sections;

        form.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: Object.prototype.hasOwnProperty.call(responseJson, 'items')
                ? responseJson.items
                : [responseJson],
              cart: cartContent,
              onSuccessDo: onSuccessDo
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: onSuccessDo,
              cart: cartContent
            }
          })
        );

        if (
          window.themeVariables &&
          window.themeVariables.settings &&
          (window.themeVariables.settings.cartType === 'page' ||
            window.themeVariables.settings.pageType === 'cart')
        ) {
          window.location.href = rootUrl() + 'cart';
        }
      } catch (error) {
        warn('Add to cart failed.', error);

        if (self._isSubmitting) {
          finish();
        }

        fail(null);
      }
    }

    _postCartAdd(body) {
      return fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      });
    }
  };

  window.customElements.define('global-product-options', GlobalProductOptions);

  /* ------------------------------------------------------------------------------------------------------------
   * Cart consistency for option child lines
   * --------------------------------------------------------------------------------------------------------- */

  /**
   * Keeps option children consistent with their parent line:
   *
   *   - a child whose parent line no longer exists is removed (covers "remove parent" on storefronts where
   *     Shopify's native nested-line removal is not available),
   *   - a child whose quantity drifted from its parent's quantity is re-synced (v1 rule: option quantity always
   *     follows main product quantity).
   *
   * Runs only when at least one `_global_options_instance` line exists, so ordinary carts are never touched.
   */
  var cartSyncInFlight = false;

  function collectCartFixes(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return null;
    }

    var parents = Object.create(null);
    var children = [];

    cart.items.forEach(function (item) {
      var properties = item.properties || {};
      var instanceId = properties[INSTANCE_PROPERTY];

      if (!instanceId) {
        return;
      }

      if (properties[ROLE_PROPERTY] === 'parent') {
        parents[instanceId] = item;
      } else if (properties[ROLE_PROPERTY] === 'child') {
        children.push(item);
      }
    });

    if (children.length === 0) {
      return null;
    }

    var updates = {};
    var hasUpdates = false;

    children.forEach(function (child) {
      var instanceId = child.properties[INSTANCE_PROPERTY];
      var parent = parents[instanceId];

      if (!parent) {
        updates[child.key] = 0;
        hasUpdates = true;
      } else if (parent.quantity !== child.quantity) {
        updates[child.key] = parent.quantity;
        hasUpdates = true;
      }
    });

    return hasUpdates ? updates : null;
  }

  async function synchroniseCart(cart) {
    if (cartSyncInFlight) {
      return;
    }

    var updates = collectCartFixes(cart);

    if (!updates) {
      return;
    }

    cartSyncInFlight = true;

    try {
      var sectionsToBundle = [];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      var response = await fetch(rootUrl() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates, sections: sectionsToBundle.join(',') })
      });

      if (!response.ok) {
        warn('Could not synchronise option lines with their parent line.');
        return;
      }

      var updatedCart = await response.json();

      if (window.themeVariables && window.themeVariables.settings.pageType === 'cart') {
        window.location.reload();
        return;
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'line-item:change',
            cart: updatedCart
          }
        })
      );
    } catch (error) {
      warn('Could not synchronise option lines with their parent line.', error);
    } finally {
      cartSyncInFlight = false;
    }
  }

  document.addEventListener('cart:change', function (event) {
    if (!event.detail || !event.detail.cart) {
      return;
    }

    if (event.detail.baseEvent === 'variant:add') {
      return; // Freshly added groups are consistent by construction.
    }

    synchroniseCart(event.detail.cart);
  });
})();
